import StepEducation from "@/pages/user/confirm-details/components/steps/StepEducation";
import StepExperience from "@/pages/user/confirm-details/components/steps/StepExperience";
import StepMoreInfo from "@/pages/user/confirm-details/components/steps/StepMoreInfo";
import StepPersonal from "@/pages/user/confirm-details/components/steps/StepPersonal";
import { ALTERNATE_EMAIL_COPY } from "@/pages/user/confirm-details/components/steps/alternateEmailCopy";
import StepSalary from "@/pages/user/confirm-details/components/steps/StepSalary";
import StepSkills from "@/pages/user/confirm-details/components/steps/StepSkills";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import ResumePreview from "./components/ResumePreview";
import { Card } from "@/components/ui/Card";
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { shortId } from "@/utils/formatText";
import { Loader2, AlertTriangle } from "lucide-react";
import { SplitReviewLayout, ReviewFormPane, ReviewStatusChip, TOOLBAR_BUTTON } from "@/components/shared/SplitReviewLayout";
import { buildSaveStatus, getFirstErrorPath } from "@/utils/reviewFormStatus";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildReviewSchema } from "@/pages/user/confirm-details/validation.schema";
import { CandidateService } from "@/services/candidateService";
import { JobService } from "@/services/jobService";
import { MatchRetriggerService } from "@/services/matchRetriggerService";
import { getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import toast from "react-hot-toast";
import RetriggerConfirmModal from "./components/RetriggerConfirmModal";
import AlertModal from "@/components/shared/AlertModal";
import { buildResumePayload } from "./buildResumePayload";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import { mapAnalysisToFormValues } from "./mapAnalysisToFormValues";
import { requiredLanguageNames } from "@/utils/languageRequirements";

const SECTIONS = [
  { key: "personal", label: "Personal Info" },
  { key: "skills", label: "Skills & Language" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "salary", label: "Salary & Availability" },
  { key: "moreinfo", label: "More Info" },
];

const FIELD_TO_SECTION = {
  personal_information: "personal",
  skills: "skills",
  experience: "experience",
  education: "education",
  compensation: "salary",
  additionalSections: "moreinfo",
};

const EditCandidateProfile = () => {
  const { candidateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setHeader = useOutletContext();
  const [searchParams] = useSearchParams();
  const sectionRefs = useRef({});

  const jobTitle = location.state?.jobTitle;
  const jobId = searchParams.get("job_id") || location.state?.jobId;
  const applicationId = searchParams.get("application_id") || location.state?.applicationId;
  const canEdit = Boolean(candidateId && jobId && applicationId);

  // Always load the current candidate data from the analysis endpoint. The
  // route state is used only for display context and cannot safely restore a
  // direct route after a reload.
  const [data, setData] = useState(null);
  const [salaryDetails, setSalaryDetails] = useState(location.state?.salaryDetails ?? null);
  // Separate from `data` (which is tabs_details.resume_confirmed_data, the
  // resume-extraction draft) — alternate_email is an application-level field
  // that lives on the response's sibling `candidate_details` object instead.
  const [candidateDetails, setCandidateDetails] = useState(null);

  const [resumeFile, setResumeFile] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState(null);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  // The job's own language rows — prefilled first and locked in the editor,
  // same as the candidate-facing apply flow (see `languagesSlot` below).
  const [jobRequiredLanguages, setJobRequiredLanguages] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingCandidate, setIsLoadingCandidate] = useState(canEdit);
  const [configError, setConfigError] = useState("");
  const [candidateLoadError, setCandidateLoadError] = useState("");
  const [reloadAttempt, setReloadAttempt] = useState(0);
  const [nextAttempt, setNextAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  // Shown BEFORE the save commits, only when the server's dry run reports that
  // score-affecting fields changed. The save is held until the user picks
  // "Save as it is" or "Retrigger and Save"; dismissing keeps them on the page
  // with nothing written. `payload` is the already-built body, stashed so the
  // modal's buttons don't have to re-run validation.
  const [retriggerModal, setRetriggerModal] = useState({
    open: false,
    changedFields: [],
    submitting: false,
    // "confirm" | "skip" | null — which button is in flight.
    submittingAction: null,
    payload: null,
  });

  // Languages render as the paired dropdowns (LanguageProficiencyRows), so
  // the skills schema must validate rows, not the tag input's strings.
  const schema = useMemo(() => buildReviewSchema(mandatoryFields, true, { languagesShape: "rows" }), [mandatoryFields]);

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const watchedValues = useWatch({ control: methods.control });
  // Same rule, same wording as the bulk review screen's save bar.
  const saveStatus = useMemo(() => buildSaveStatus(watchedValues, mandatoryFields, methods.formState.errors), [watchedValues, mandatoryFields, methods.formState.errors]);

  const scrollToSection = useCallback((key) => {
    const el = sectionRefs.current[key];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSection(key);

    setTimeout(() => {
      setHighlightedSection((current) => (current === key ? null : current));
    }, 2000);
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!canEdit) {
        setIsLoadingConfig(false);
        return;
      }
      setIsLoadingConfig(true);
      setConfigError("");
      try {
        const [res, jobRes] = await Promise.all([JobService.fetchJobFieldConfig(jobId), JobService.fetchJobById(jobId).catch(() => null)]);
        if (!res?.success || !res?.data) throw new Error("Job field configuration is unavailable.");
        const all = [...(res.data.always_mandatory || []).map((f) => f.key), ...(res.data.configured_mandatory || []).map((f) => f.key)];
        setMandatoryFields(all);
        setJobRequiredLanguages(jobRes?.data?.languages_required || []);
      } catch (err) {
        console.error("Failed to fetch field config:", err);
        setConfigError(apiErrorMessageFormat(err, "Unable to load the job field configuration. Please try again."));
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchConfig();
  }, [canEdit, jobId, reloadAttempt]);

  // Refetch the freshest candidate detail on mount. Navigation state is a
  // point-in-time snapshot of the analysis page's Redux cache, which may not
  // reflect a just-saved edit; the source of truth is /candidate/detail/.
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    setIsLoadingCandidate(true);
    setCandidateLoadError("");
    (async () => {
      try {
        const res = await CandidateService.fetchAnalysis({ candidateId, jobId });
        if (cancelled) return;
        const tabs = res?.tabs_details;
        if (!tabs?.resume_confirmed_data) {
          throw new Error("Candidate details are unavailable.");
        }
        setData(tabs.resume_confirmed_data);
        if (tabs?.salary_details) setSalaryDetails(tabs.salary_details);
        setCandidateDetails(res?.candidate_details ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to refresh candidate detail on edit page:", err);
        setCandidateLoadError(apiErrorMessageFormat(err, "Unable to load candidate details. Please try again."));
      } finally {
        if (!cancelled) setIsLoadingCandidate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit, candidateId, jobId, reloadAttempt]);

  useEffect(() => {
    if (!data) return;

    methods.reset(mapAnalysisToFormValues({ data, salaryDetails, candidateDetails, jobRequiredLanguages }));
  }, [data, salaryDetails, candidateDetails, jobRequiredLanguages, methods]);

  useEffect(() => {
    if (!data) return;

    setHeader({
      title: "Compare & Edit Mode",
      subTitle: (
        <>
          {data?.personal_information?.full_name || "Candidate Name: N/A"} | {jobTitle || "Job Title: N/A"}
          {jobId != null && <span className="font-mono opacity-70"> ({shortId(jobId)})</span>}
        </>
      ),
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [data, jobTitle, jobId, setHeader]);

  const extractFileName = (contentDisposition, contentType = "") => {
    if (!contentDisposition) {
      if (contentType.includes("pdf")) return "resume.pdf";
      if (contentType.includes("word") || contentType.includes("officedocument")) return "resume.docx";
      return "resume";
    }

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (asciiMatch?.[1]) {
      return asciiMatch[1];
    }

    if (contentType.includes("pdf")) return "resume.pdf";
    if (contentType.includes("word") || contentType.includes("officedocument")) return "resume.docx";
    return "resume";
  };

  const fetchResume = useCallback(async () => {
    if (!candidateId || !applicationId) return;

    try {
      const res = await CandidateService.fetchCandidateResume(candidateId, applicationId);

      const contentType = res?.headers?.["content-type"] || "";
      const contentDisposition = res?.headers?.["content-disposition"] || "";
      const filename = extractFileName(contentDisposition, contentType);

      const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: contentType });

      const objectUrl = URL.createObjectURL(blob);

      setResumeFile((prev) => {
        if (prev?.objectUrl) {
          URL.revokeObjectURL(prev.objectUrl);
        }

        return {
          filename,
          objectUrl,
          blob,
          type: contentType,
        };
      });
    } catch (error) {
      console.error("fetchResume error:", error);
      toast.error(apiErrorMessageFormat(error, "Failed to load resume preview."));
    }
  }, [candidateId, applicationId]);

  const handleDownloadResume = useCallback(async () => {
    if (!candidateId || !applicationId) return;
    try {
      await CandidateService.downloadCandidateResume(candidateId, applicationId);
    } catch (error) {
      console.error("downloadResume error:", error);
      toast.error(apiErrorMessageFormat(error, "Failed to download resume."));
    }
  }, [candidateId, applicationId]);

  const leaveToAnalysis = useCallback(() => {
    // Pop the real history entry when there is one: the details page sits
    // directly behind this route (List → Details → Edit), so navigate(-1)
    // restores the exact stack the user expects — one Back from Details
    // reaches the list, and no Edit copy lingers to loop or duplicate.
    // Replacing here instead stacked a second identical Details entry, which
    // made the first Back press look like it did nothing.
    const idx = window.history.state?.idx;
    if (Number.isInteger(idx) && idx > 0) {
      navigate(-1);
      return;
    }
    // Direct-opened URL (new tab / bookmark): nothing in-app to pop, so
    // land explicitly and keep this page out of the history stack.
    if (!jobId) {
      navigate("/admin/candidates", { replace: true });
      return;
    }
    navigate(`/admin/candidate-analysis/${candidateId}?job_id=${jobId}`, {
      replace: true,
    });
  }, [candidateId, jobId, navigate]);

  const retryLoading = useCallback(() => {
    setReloadAttempt((attempt) => attempt + 1);
  }, []);

  // Dismiss ("Cancel" / "X") — nothing has been saved and nothing will be.
  // The user stays in Compare & Edit Mode with their edits intact, so they can
  // keep working or leave deliberately via the page's own Cancel button.
  const dismissRetriggerModal = useCallback(() => {
    setRetriggerModal({
      open: false,
      changedFields: [],
      submitting: false,
      submittingAction: null,
      payload: null,
    });
  }, []);

  // The one place the PATCH is actually issued. `retrigger` decides whether a
  // 1-item batch is queued afterwards, through the SAME endpoint the JD picker
  // uses. A re-trigger failure must never look like a failed save (E11), so it
  // only downgrades the toast — the save still stands and we still navigate.
  const commitSave = useCallback(
    async (payload, { retrigger, changedFields = [] }) => {
      try {
        await CandidateService.updateCandidateResume(candidateId, applicationId, payload);
      } catch (err) {
        console.error("onSave error:", err);
        toast.error(apiErrorMessageFormat(err, "Failed to save changes."));
        return false;
      }

      if (!retrigger) {
        toast.success("Details Updated Successfully");
        leaveToAnalysis();
        return true;
      }

      try {
        await MatchRetriggerService.createBatch({
          triggerSource: "CANDIDATE_EDIT",
          jobId,
          applicationIds: [applicationId],
          changedFields,
          reason: changedFields.length ? `Profile updated: ${changedFields.join(", ")}` : "Profile updated",
        });
        toast.success("Details updated. Re-analysis started — the score will update shortly.");
      } catch (err) {
        console.error("Failed to queue re-analysis:", err);
        toast.error(apiErrorMessageFormat(err, "Saved, but re-analysis could not be started. You can retry from the candidate page."));
      }

      leaveToAnalysis();
      return true;
    },
    [candidateId, applicationId, jobId, leaveToAnalysis],
  );

  // "Save as it is" — commit the held payload, skip the re-analysis.
  // `submittingAction` tracks WHICH button is in flight so the modal puts its
  // pending label on the one the user pressed, not on the other one.
  const handleSkipRetrigger = useCallback(async () => {
    const payload = retriggerModal.payload;
    if (!payload) return;
    setRetriggerModal((prev) => ({ ...prev, submitting: true, submittingAction: "skip" }));
    const ok = await commitSave(payload, { retrigger: false });
    // On failure keep the modal open (un-submitting) so the user can retry;
    // on success we're navigating away, so tearing it down is just cleanup.
    setRetriggerModal((prev) =>
      ok ? { open: false, changedFields: [], submitting: false, submittingAction: null, payload: null } : { ...prev, submitting: false, submittingAction: null },
    );
  }, [retriggerModal.payload, commitSave]);

  // "Retrigger and Save" — commit the held payload, then queue the re-analysis.
  const handleConfirmRetrigger = useCallback(async () => {
    const payload = retriggerModal.payload;
    if (!payload) return;
    const changedFields = retriggerModal.changedFields;
    setRetriggerModal((prev) => ({ ...prev, submitting: true, submittingAction: "confirm" }));
    const ok = await commitSave(payload, { retrigger: true, changedFields });
    setRetriggerModal((prev) =>
      ok ? { open: false, changedFields: [], submitting: false, submittingAction: null, payload: null } : { ...prev, submitting: false, submittingAction: null },
    );
  }, [retriggerModal.payload, retriggerModal.changedFields, commitSave]);

  const onSubmit = methods.handleSubmit(
    // ── Success ──
    async (formData) => {
      const payload = buildResumePayload(formData);

      setIsSaving(true);
      try {
        // Ask FIRST, save second. The dry run writes nothing; it only reports
        // whether this edit touches a field that reaches the match prompt.
        // Score-affecting => hold the save behind the modal. Otherwise (a
        // NON_SCORE_AFFECTING_CANDIDATE_KEYS-only edit, e.g. a phone-number
        // fix) save straight through, exactly as before this feature existed.
        //
        // camelCase: matchRetrigger is a typed Pydantic model on this endpoint,
        // so it serialises with aliases (unlike the job PUT, whose data is an
        // untyped Dict[str, Any] passthrough that keeps snake_case).
        let block = null;
        try {
          const preview = await CandidateService.previewCandidateResumeRetrigger(candidateId, applicationId, payload);
          block = preview?.matchRetrigger;
        } catch (err) {
          // The preview is an enhancement, not a gate. If it fails, fall back to
          // the plain save rather than blocking the user's edit.
          console.error("Retrigger preview failed; saving without prompt:", err);
        }

        if (block?.canRetrigger && block?.changedScoreFields?.length) {
          setRetriggerModal({
            open: true,
            changedFields: block.changedScoreFields,
            submitting: false,
            submittingAction: null,
            payload,
          });
          return;
        }

        await commitSave(payload, { retrigger: false });
      } finally {
        setIsSaving(false);
      }
    },

    // ── Error: runs when validation fails ──
    (errors) => {
      setNextAttempt((prev) => prev + 1);
      const messages = [];
      const collect = (obj) => {
        for (const [, val] of Object.entries(obj)) {
          if (val?.message) messages.push(val.message);
          else if (val && typeof val === "object") collect(val);
        }
      };
      collect(errors);

      toast.error(messages.length > 0 ? messages[0] : "Please fix the errors above.", {
        duration: 4000,
      });

      const firstPath = getFirstErrorPath(errors);
      if (firstPath) {
        const el = document.querySelector(`[data-name="${firstPath}"]`) ?? document.querySelector(`[name="${firstPath}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        else scrollToSection(FIELD_TO_SECTION[firstPath.split(".")[0]]);
      }
    },
  );
  // This is the toolbar's back button, so it has to behave like one: ask only
  // when there is work to lose. `methods.reset` sets the loaded values as the
  // defaults, so isDirty is false until the reviewer actually edits something.
  const onCancelClick = () => {
    if (methods.formState.isDirty) setShowCancelModal(true);
    else confirmCancel();
  };

  const confirmCancel = () => {
    leaveToAnalysis();
  };

  useEffect(() => {
    if (canEdit && data) fetchResume();

    return () => {
      setResumeFile((prev) => {
        if (prev?.objectUrl) {
          URL.revokeObjectURL(prev.objectUrl);
        }
        return null;
      });
    };
  }, [canEdit, data, fetchResume]);

  const sectionCardClass = (key) =>
    `bg-background-secondary rounded-xl border p-5 transition-all duration-500 ${
      highlightedSection === key ? "border-blue-400 shadow-[0_0_0_3px_rgba(96,165,250,0.3)]" : "border-border-primary"
    }`;

  if (!canEdit) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-20 text-center">
        <AlertTriangle className="mb-3 text-status-warning" size={32} aria-hidden="true" />
        <h1 className="text-lg font-semibold text-text-primary">Compare & Edit needs application context</h1>
        <p className="mt-2 text-sm text-text-secondary">
          This link does not include the job and application needed to load and save this candidate safely. Return to the candidate details page and choose Edit Details
          again.
        </p>
        <Button className="mt-5 rounded-lg bg-button-secondary px-4 py-2 text-sm text-text-base" onClick={() => leaveToAnalysis()}>
          {jobId ? "Back to Candidate Details" : "Back to Candidates"}
        </Button>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-text-secondary" />
      </div>
    );
  }

  const loadError = configError || candidateLoadError;
  if (loadError || !data) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-20 text-center">
        <AlertTriangle className="mb-3 text-status-warning" size={32} aria-hidden="true" />
        <h1 className="text-lg font-semibold text-text-primary">Unable to load Compare & Edit</h1>
        <p className="mt-2 text-sm text-text-secondary">{loadError || "Candidate details are unavailable. Please try again."}</p>
        <div className="mt-5 flex gap-3">
          <Button className="rounded-lg border border-border-primary px-4 py-2 text-sm text-text-primary" onClick={retryLoading}>
            Try again
          </Button>
          <Button className="rounded-lg bg-button-secondary px-4 py-2 text-sm text-text-base" onClick={leaveToAnalysis}>
            Back to Candidate Details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <SplitReviewLayout
        onBack={onCancelClick}
        backLabel="Back to candidate"
        left={
          <ReviewFormPane
            sections={SECTIONS}
            onSectionClick={scrollToSection}
            footer={
              <div className="flex flex-wrap items-center gap-2">
                <ReviewStatusChip label={saveStatus.label} tone={saveStatus.tone} />

                <div className="ml-auto flex items-center gap-2">
                  {/* No Cancel here — the back button in the toolbar is the way
                      out, and it asks before discarding. */}
                  <Button onClick={onSubmit} disabled={isSaving || retriggerModal.submitting} className={cn(TOOLBAR_BUTTON, "bg-button-secondary text-text-base")}>
                    {isSaving ? "Checking…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            }
          >
            <Card ref={(el) => (sectionRefs.current.personal = el)} className={sectionCardClass("personal")}>
              <StepPersonal mandatoryFields={mandatoryFields} alternateEmailHelperText={ALTERNATE_EMAIL_COPY.editProfile.helperText} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.skills = el)} className={sectionCardClass("skills")}>
              <StepSkills
                mandatoryFields={mandatoryFields}
                languagesSlot={(disabled, required) => (
                  <LanguageProficiencyRows
                    name="skills.languages"
                    disabled={disabled}
                    emptyText="No languages added."
                    allowUnknownProficiency
                    lockedLanguages={requiredLanguageNames(jobRequiredLanguages)}
                    languageRequired={required?.language}
                    levelledLanguages={jobRequiredLanguages}
                  />
                )}
              />
            </Card>

            <Card ref={(el) => (sectionRefs.current.experience = el)} className={sectionCardClass("experience")}>
              <StepExperience mandatoryFields={mandatoryFields} nextAttempt={nextAttempt} autoCalculate={false} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.education = el)} className={sectionCardClass("education")}>
              <StepEducation mandatoryFields={mandatoryFields} nextAttempt={nextAttempt} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.salary = el)} className={sectionCardClass("salary")}>
              <StepSalary
                mandatoryFields={mandatoryFields}
                autoCalculate={false}
                currencySign={salaryDetails?.currency_sign || getDefaultCurrencyFallback().currencySign}
              />
            </Card>

            <Card ref={(el) => (sectionRefs.current.moreinfo = el)} className={sectionCardClass("moreinfo")}>
              <StepMoreInfo showSocials={false} mandatoryFields={mandatoryFields} />
            </Card>
          </ReviewFormPane>
        }
        right={<ResumePreview file={resumeFile} onDownload={handleDownloadResume} />}
      />

      {/* The same discard prompt the other review screens use — AlertModal,
          not a hand-rolled overlay with raw buttons. */}
      <AlertModal
        open={showCancelModal}
        title="Discard changes?"
        description="You have unsaved changes. If you leave now, all updates will be lost."
        cancelText="Keep editing"
        actionText="Discard"
        onOpenChange={(open) => !open && setShowCancelModal(false)}
        onCancel={() => setShowCancelModal(false)}
        onAction={confirmCancel}
        actionClassName="bg-tone-rose text-text-base"
      />

      <RetriggerConfirmModal
        open={retriggerModal.open}
        changedFields={retriggerModal.changedFields}
        submitting={retriggerModal.submitting}
        submittingAction={retriggerModal.submittingAction}
        jobTitle={jobTitle}
        onDismiss={dismissRetriggerModal}
        onSkip={handleSkipRetrigger}
        onConfirm={handleConfirmRetrigger}
      />
    </FormProvider>
  );
};

export default EditCandidateProfile;
